import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from "@/components/ui/sidebar";
import {
  Home,
  BookOpen,
  Clock,
  BarChart3,
  Settings,
  Smartphone,
  Shield,
  Users,
  FileQuestion,
  Activity,
  HelpCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/useAuth";
import { useLanguage } from "@/contexts/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { useSidebar } from "../ui/use-sidebar";

const getUserNavItems = (t: (key: string) => string) => [
  {
    title: t("nav.dashboard"),
    url: "/dashboard",
    icon: Home,
    badge: null
  },
  {
    title: t("nav.practice"),
    url: "/dashboard/practice",
    icon: BookOpen,
    badge: null
  },
  {
    title: t("nav.exams"),
    url: "/dashboard/exams",
    icon: Clock,
    badge: null
  },
  {
    title: t("nav.progress"),
    url: "/dashboard/progress",
    icon: BarChart3,
    badge: null
  },
  {
    title: t("nav.settings"),
    url: "/dashboard/settings",
    icon: Settings,
    badge: null
  },
  {
    title: t("nav.devices"),
    url: "/dashboard/devices",
    icon: Smartphone,
    badge: null
  }
];

const getAdminNavItems = (t: (key: string) => string) => [
  {
    title: t("nav.dashboard"),
    url: "/admin/dashboard",
    icon: Home,
    badge: null
  },
  {
    title: t("nav.userManagement"),
    url: "/admin/users",
    icon: Users,
    badge: null
  },
  {
    title: t("nav.questionManagement"),
    url: "/admin/questions",
    icon: FileQuestion,
    badge: null
  },
  {
    title: t("nav.sessionManagement"),
    url: "/admin/sessions",
    icon: Activity,
    badge: null
  },
  {
    title: t("nav.adminSettings"),
    url: "/admin/settings",
    icon: Settings,
    badge: null
  }
];

interface AppSidebarProps {
  isAdmin?: boolean;
}

export function AppSidebar({ isAdmin = false }: AppSidebarProps) {
  const { state } = useSidebar();
  const location = useLocation();
  const { user, userRole } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState({ score: 0, totalSessions: 0, loading: true });
  
  const currentPath = location.pathname;
  const navItems = isAdmin ? getAdminNavItems(t) : getUserNavItems(t);
  const isAdminUser = userRole === 'admin';

  // Add admin link for regular users if they have admin role
  const finalNavItems = !isAdmin && isAdminUser ? [
    ...navItems,
    {
      title: "Admin",
      url: "/admin/dashboard",
      icon: Shield,
      badge: "Admin"
    }
  ] : navItems;

  const isActive = (path: string) => currentPath === path;
  const isCollapsed = state === "collapsed";

  useEffect(() => {
    let mounted = true;
    
    const fetchUserStats = async () => {
      if (!user || !mounted) return;

      try {
        if (isAdminUser) {
          // For admins, fetch system stats
          const { data: totalUsers, error: usersError } = await supabase
            .from('profiles')
            .select('id', { count: 'exact' });

          const { data: activeSessions, error: sessionsError } = await supabase
            .from('exam_sessions')
            .select('id', { count: 'exact' })
            .eq('status', 'in_progress');

          if (!usersError && !sessionsError && mounted) {
            setStats({
              score: totalUsers?.length || 0,
              totalSessions: activeSessions?.length || 0,
              loading: false
            });
          }
        } else {
          // For regular users, fetch their exam performance
          const { data: userSessions, error } = await supabase
            .from('exam_sessions')
            .select('score, status')
            .eq('user_id', user.id)
            .eq('status', 'completed');

          if (!error && userSessions && mounted) {
            const completedSessions = userSessions.filter(session => session.score !== null);
            const averageScore = completedSessions.length > 0 
              ? completedSessions.reduce((sum, session) => sum + (session.score || 0), 0) / completedSessions.length
              : 0;

            setStats({
              score: Math.round(averageScore),
              totalSessions: completedSessions.length,
              loading: false
            });
          }
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        if (mounted) {
          setStats(prev => ({ ...prev, loading: false }));
        }
      }
    };

    fetchUserStats();
    
    return () => {
      mounted = false;
    };
  }, [user?.id, isAdminUser]); // Only depend on user ID and admin status

  return (
    <Sidebar collapsible="icon" className="border-r border-border/40">
      <SidebarHeader className="border-b border-border/40 p-4 md:p-6">
        {!isCollapsed && (
          <div className="flex items-center space-x-3">
            <div className="flex h-8 w-8 md:h-10 md:w-10 items-center justify-center rounded-lg">
              <img 
                src="/nostrimed-uploads/4a2132fa-fc6e-4ccb-8d59-579ae55dac41.png" 
                alt="NostriMed Logo" 
                className="h-6 w-6 md:h-8 md:w-8"
                width="32"
                height="32"
                loading="lazy"
              />
            </div>
            <div>
              <h1 className="text-base md:text-lg font-semibold text-foreground">NostriMed</h1>
              <p className="text-xs text-muted-foreground">
                {isAdmin ? "Admin Panel" : "Medical Platform"}
              </p>
            </div>
          </div>
        )}
        {isCollapsed && (
          <div className="flex justify-center">
            <div className="flex h-6 w-6 md:h-8 md:w-8 items-center justify-center rounded-lg">
              <img 
                src="/nostrimed-uploads/4a2132fa-fc6e-4ccb-8d59-579ae55dac41.png" 
                alt="NostriMed Logo" 
                className="h-5 w-5 md:h-6 md:w-6"
                width="24"
                height="24"
                loading="lazy"
              />
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="px-3 py-4 md:px-4 md:py-6">
        <SidebarGroup>
          <SidebarGroupLabel className="px-2 md:px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {isAdmin ? "Admin Tools" : "Navigation"}
          </SidebarGroupLabel>

          <SidebarGroupContent className="mt-3 md:mt-4">
            <SidebarMenu className="space-y-1 md:space-y-2">
              {finalNavItems.map((item, index) => {
                const active = isActive(item.url);
                return (
                  <SidebarMenuItem key={`${item.url}-${index}`}>
                    <SidebarMenuButton asChild className="h-auto p-0">
                      <NavLink 
                        to={item.url} 
                        end 
                        className={`group relative flex w-full items-center space-x-2 md:space-x-3 rounded-xl px-2 md:px-3 py-2 md:py-3 text-sm font-medium transition-all duration-200 ${
                          active 
                            ? "bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20" 
                            : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                        }`}
                      >
                        <div className={`flex h-8 w-8 md:h-9 md:w-9 items-center justify-center rounded-lg transition-colors ${
                          active 
                            ? "bg-primary text-primary-foreground shadow-sm" 
                            : "bg-muted group-hover:bg-muted-foreground/10"
                        }`}>
                          <item.icon className="h-4 w-4" />
                        </div>
                        
                        {!isCollapsed && (
                          <div className="flex-1 min-w-0">
                            <span className="block truncate">{item.title}</span>
                          </div>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        
        {/* Quick Stats Section for non-collapsed sidebar */}
        {!isCollapsed && (
          <SidebarGroup className="mt-6 md:mt-8">
            <SidebarGroupLabel className="px-2 md:px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Quick Stats
            </SidebarGroupLabel>
            <SidebarGroupContent className="mt-3 md:mt-4">
              <div className="rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 p-3 md:p-4 border border-primary/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm font-medium text-foreground">
                      {isAdminUser ? "System Overview" : "Performance"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isAdminUser 
                        ? `${stats.totalSessions} active sessions` 
                        : stats.totalSessions > 0 
                          ? `${stats.totalSessions} exams completed`
                          : "Start practicing to see your progress!"
                      }
                    </p>
                  </div>
                  <div className="h-6 w-6 md:h-8 md:w-8 rounded-lg bg-primary/20 flex items-center justify-center">
                    <BarChart3 className="h-3 w-3 md:h-4 md:w-4 text-primary" />
                  </div>
                </div>
                
                <div className="mt-2 md:mt-3 flex items-center justify-between">
                  <span className="text-base md:text-lg font-bold text-primary">
                    {stats.loading ? "..." : isAdminUser ? `${stats.score}` : `${stats.score}%`}
                  </span>
                  <span className="text-xs text-green-600 font-medium">
                    {isAdminUser ? "Users" : "Avg Score"}
                  </span>
                </div>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Support Section at Bottom */}
        {!isAdmin && (
          <div className="mt-auto">
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild className="h-auto p-0">
                      <NavLink 
                        to="/dashboard/support" 
                        className={`group relative flex w-full items-center space-x-2 md:space-x-3 rounded-xl px-2 md:px-3 py-2 md:py-3 text-sm font-medium transition-all duration-200 ${
                          isActive("/dashboard/support")
                            ? "bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20" 
                            : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                        }`}
                      >
                        <div className={`flex h-8 w-8 md:h-9 md:w-9 items-center justify-center rounded-lg transition-colors ${
                          isActive("/dashboard/support")
                            ? "bg-primary text-primary-foreground shadow-sm" 
                            : "bg-muted group-hover:bg-muted-foreground/10"
                        }`}>
                          <HelpCircle className="h-4 w-4" />
                        </div>
                        
                        {!isCollapsed && (
                          <div className="flex-1 min-w-0">
                            <span className="block truncate">Support</span>
                          </div>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}